import React, { useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { typesReports } from "../utils/constants";
import {
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useSample } from "../../samples/utils/useSample";
import DateRangeWeek from "./subForms/dateRangeWeek";
import DateRangeForm from "./subForms/dateRangeForm";
import { MODES_OPTIONS } from "@app/types/generics.types";
import {
  useAnalysisTypes,
  useClient,
  useInstallation,
  useIsotopos,
  useReceiver,
  useSamplingReason,
  useTypesAccumulation,
  useTypeSample,
  useUser,
} from "@services/servicesGenericsTables";
import useFilteredSamplingPoints from "@app/hooks/useFilteredSamplingPoints";
import {
  MAutocomplete,
  MCheckbox,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { yesNoTable } from "@app/utils/tables/shared.tables";
import AnexosCarta from "./subForms/anexoCarta";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";

interface ReportLidtFormProps {
  mode: MODES_OPTIONS;
}

const ReportResultForm = ({ mode }: ReportLidtFormProps) => {
  const form = useFormContext();
  const { idReport } = useParams();
  const searchType = form.watch("searchType") || "dateRange";
  const isView = mode === MODES_OPTIONS.VIEW;

  const { typesAccumulationList } = useTypesAccumulation();
  const { maslReferenceList, clientReferenceList } = useSample();

  const { samplingReasonList } = useSamplingReason();
  const { sampleTypeList } = useTypeSample();
  const { clientsList } = useClient();
  const { receiverList } = useReceiver();
  const { installationList } = useInstallation();
  const { userList } = useUser();
  const { analysisTypeList } = useAnalysisTypes();
  const { isotoposList } = useIsotopos();

  const installation = form.watch("report_installation");
  const filteredSamplingPoints = useFilteredSamplingPoints(
    installation,
    "punto_muestreo_id"
  );

  const acumulacionValue = form.watch("acumulacion");

  // Establecer todos los tipos de análisis por defecto en modo creación
  useEffect(() => {
    if (!idReport && analysisTypeList.length > 0) {
      const currentValue = form.getValues("reporte_tipos_analisis");
      // Solo establecer valores por defecto si no hay valores previamente establecidos
      if (!currentValue || currentValue.length === 0) {
        const allAnalysisTypeIds = analysisTypeList.map((item) => item.id);
        form.setValue("reporte_tipos_analisis", allAnalysisTypeIds, { shouldValidate: false });
      }
    }
  }, [analysisTypeList, idReport, form]);

  // Sincronizar el valor de acumulacion cuando cambie la lista de tipos de acumulación
  useEffect(() => {
    const currentValue = form.getValues("acumulacion");
    if (currentValue && typeof currentValue === 'number' && typesAccumulationList.length > 0) {
      const acumulacionObj = typesAccumulationList.find(item => item.id === currentValue);
      if (acumulacionObj) {
        form.setValue("acumulacion", acumulacionObj, { shouldValidate: false });
      }
    }
  }, [typesAccumulationList, form]);

  // Asegurar que el valor se sincroniza cuando cambia
  useEffect(() => {
    if (acumulacionValue && typeof acumulacionValue === 'number' && typesAccumulationList.length > 0) {
      const acumulacionObj = typesAccumulationList.find(item => item.id === acumulacionValue);
      if (acumulacionObj && acumulacionValue !== acumulacionObj) {
        form.setValue("acumulacion", acumulacionObj, { shouldValidate: false });
      }
    }
  }, [acumulacionValue, typesAccumulationList]);

  useEffect(() => {
    if (idReport) {
      form.setValue("searchType", form.getValues("searchType") || "dateRange");
    }
  }, [idReport]);

  const changeTypeDate = (type: string) => {
    if (type === "dateRange") {
      const today = dayjs();
      form.setValue("year_pickup", undefined);
      form.setValue("start_week_pickup", undefined);
      form.setValue("end_week_pickup", undefined);
      form.setValue("evo_start_date", today);
      form.setValue("evo_end_date", today);
    } else {
      form.setValue("evo_start_date", undefined);
      form.setValue("evo_end_date", undefined);
    }
    form.setValue("searchType", type);
  };

  return (
    <>
      <ResponsiveFormContainer columns={1} mb={4}>
        <MCheckbox
          key={JSON.stringify(form.getValues("report_lid"))}
          name="report_lid"
          options={typesReports}
          multiple={false}
          orientation="horizontal"
          columns={5}
          disabled={isView}
        />
      </ResponsiveFormContainer>
      <AnexosCarta
        mode={mode}
        idReport={idReport ? Number(idReport) : undefined}
      />
      <ResponsiveFormContainer columns={5} mt={2}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("client"))}
          name="client"
          options={clientsList}
          label="Cliente"
          getOptionLabel={(option) => option.entidad}
          validation={{ required: "El cliente es requerido" }}
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("destinatario_id"))}
          name="destinatario_id"
          options={receiverList}
          label="Destinatario"
          getOptionLabel={(option) => option.nombre}
          validation={{ required: "El destinatario es requerido" }}
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("report_installation"))}
          name="report_installation"
          options={installationList}
          label="Instalación"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "La instalación es requerida" }}
          disabled={isView}
        />

        <MAutocomplete
          key={JSON.stringify(form.getValues("approved_by"))}
          name="approved_by"
          options={userList}
          label="Aprobado por"
          getOptionLabel={(option) => option.username || ""}
          validation={{ required: "El analista es requerido" }}
          disabled={isView}
        />
        <MDatePicker
          name="emission_date"
          pickerType="date"
          label="Fecha de emisión"
          validation={{
            required: "La fecha de emisión es requerida",
          }}
          disabled={isView}
        />
      </ResponsiveFormContainer>
      <ResponsiveFormContainer columns={2} mt={2}>
        <MTextField
          key={JSON.stringify(form.getValues("subtitle"))}
          name="subtitle"
          label="Subtítulo"
          type="textArea"
          rows={4}
          multiline
          fullWidth
          disabled={isView}
        />
      </ResponsiveFormContainer>
      <Grid item xs={12} my={3}>
        <Typography variant="tagText">{"Búsqueda de Muestras"}</Typography>
      </Grid>
      <ResponsiveFormContainer columns={5}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("masl_reference"))}
          name="masl_reference"
          options={maslReferenceList || []}
          label="Referencia MASL"
          getOptionLabel={(option) => String(option)}
          freeSolo
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("client_reference"))}
          name="client_reference"
          options={(clientReferenceList || []).filter((item) => item !== null && item !== undefined && item !== "")}
          label="Referencia cliente"
          getOptionLabel={(option) => String(option)}
          freeSolo
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("motivo_muestreo_id"))}
          name="motivo_muestreo_id"
          options={samplingReasonList}
          label="Seleccione un motivo"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(filteredSamplingPoints)}
          name="punto_muestreo_id"
          options={filteredSamplingPoints}
          label={"Punto de muestreo"}
          helperText={!installation ? "Seleccione una instalación" : ""}
          placeholder="Punto de muestreo"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          disabled={!installation || isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("tipo_muestra_id"))}
          name="tipo_muestra_id"
          options={sampleTypeList}
          label="Tipo de muestra"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("shared"))}
          name="shared"
          options={yesNoTable}
          label="Seleccione si es compartida"
          getOptionLabel={(option) => option.label || ""}
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("acumulacion"))}
          name="acumulacion"
          options={typesAccumulationList}
          label="Acumulación"
          getOptionLabel={(option) => {
            if (!option || typeof option !== 'object') return '';
            return `${option.code || ''} - ${option.name || ''}`.replace(/^\s*-\s*$/, '');
          }}
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("reporte_isotopos"))}
          name="reporte_isotopos"
          options={isotoposList}
          label="Isótopos (IG)"
          getOptionLabel={(option) => `${option.codigo}`}
          multiple={true}
          disabled={isView}
        />
      </ResponsiveFormContainer>

      <ResponsiveFormContainer columns={1} my={5}>
        <MCheckbox
          key={JSON.stringify(form.getValues("reporte_tipos_analisis"))}
          name="reporte_tipos_analisis"
          options={analysisTypeList.map((item) => ({
            value: item.id,
            label: item.codigo,
          }))}
          validation={{ required: "Debe seleccionar al menos una opción" }}
          multiple={true}
          orientation="horizontal"
          columns={5}
          disabled={isView}
        />
      </ResponsiveFormContainer>

      <Controller
        name="searchType"
        control={form.control}
        defaultValue={searchType}
        render={({ field }) => (
          <RadioGroup
            {...field}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value);
              changeTypeDate(e.target.value);
            }}
            row
          >
            <FormControlLabel
              value="dateRange"
              control={<Radio />}
              label="Rango de fechas"
              disabled={isView}
            />
            <FormControlLabel
              value="dateRangeWeek"
              control={<Radio />}
              label="Rango de semana"
              disabled={isView}
            />
          </RadioGroup>
        )}
      />

      {form.getValues("searchType") === "dateRange" && (
        <DateRangeForm mode={mode} key={`form-${searchType}`} />
      )}
      {form.getValues("searchType") === "dateRangeWeek" && (
        <DateRangeWeek mode={mode} key={`form-${searchType}`} />
      )}

      <ResponsiveFormContainer columns={2} mt={2}>
        <MTextField
          name="comments"
          label="Comentarios"
          placeholder="Comentarios"
          type="textArea"
          rows={4}
          multiline
          fullWidth
          disabled={isView}
          step={0.01}
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default ReportResultForm;
